import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Card,
  CardContent,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Person,
  Phone,
  Notes,
  Schedule,
  LocationOn,
  CalendarMonth,
  Groups,
  Refresh,
  QrCode2,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import QRCode from 'qrcode';

interface Passenger {
  nickname: string;
  seats: number;
  phone: string | null;
  notes: string | null;
  booking_code: string;
  checked_in: boolean;
}

interface DriverViewData {
  schedule: {
    id: string;
    departure_date: string;
    departure_time: string;
    pickup_location: string;
    destination: string;
    status: string;
    notes: string | null;
  };
  shuttle: {
    name: string;
    capacity: number;
  };
  club: {
    id: string;
    name: string;
  };
  summary: {
    total_passengers: number;
    total_seats_booked: number;
    available_seats: number;
    occupancy_percentage: number;
  };
  passengers: Passenger[];
  driver_url: string;
}

const DriverViewPage: React.FC = () => {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const { t, i18n, ready } = useTranslation();
  const [data, setData] = useState<DriverViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  
  // Get the locale for date formatting
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const fetchDriverView = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/public/schedules/${scheduleId}/driver-view`);
      
      if (!response.ok) {
        throw new Error(t('driver.errors.fetchData'));
      }
      
      const result = await response.json();
      setData(result);
      setLastRefresh(new Date());
    } catch (error: any) {
      console.error('Failed to fetch driver view:', error);
      setError(error.message || t('driver.errors.fetchData'));
    } finally {
      setLoading(false);
    }
  }, [scheduleId, t, apiUrl]);

  useEffect(() => {
    fetchDriverView();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDriverView, 30000);
    return () => clearInterval(interval);
  }, [scheduleId, fetchDriverView]);

  useEffect(() => {
    // Generate QR code for this page
    const currentUrl = window.location.href;
    QRCode.toDataURL(currentUrl, { width: 300 }).then(setQrCodeUrl);
  }, []);

  const handleRefresh = () => {
    fetchDriverView();
  };

  // Wait for translations to be ready
  if (!ready || (loading && !data)) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('driver.errors.dataNotFound')}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {t('common.backToHome')}
        </Button>
      </Container>
    );
  }

  const departureDate = new Date(data.schedule.departure_date);
  const occupancyColor = data.summary.occupancy_percentage > 80 ? 'warning' : 'success';

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 2, px: { xs: 1, sm: 2 } }}>
        {/* Navigation and action buttons */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const urlParams = new URLSearchParams(window.location.search);
                const clubId = data?.club?.id || urlParams.get('club');
                if (clubId) {
                  navigate(`/driver?club=${clubId}`);
                } else {
                  navigate('/driver');
                }
              }}
            >
              {t('driver.backToSchedules') || 'Back to Schedules'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/app/shuttle-booking')}
            >
              {t('shuttle.bookingPortal') || 'Booking Portal'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              color="primary" 
              onClick={handleRefresh}
              sx={{ border: '1px solid', borderColor: 'primary.main' }}
            >
              <Refresh />
            </IconButton>
            <IconButton 
              color="primary" 
              onClick={() => setShowQR(!showQR)}
              sx={{ border: '1px solid', borderColor: 'primary.main' }}
            >
              <QrCode2 />
            </IconButton>
          </Box>
        </Box>
        
        {/* QR Code Display */}
        {showQR && (
          <Paper elevation={3} sx={{ p: 2, mb: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('driver.shareQR')}
            </Typography>
            <img src={qrCodeUrl} alt="QR Code" style={{ maxWidth: '200px' }} />
          </Paper>
        )}

        {/* Status Summary Card */}
        <Card sx={{ mb: 2, backgroundColor: 'primary.50' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" component="div">
                {data.shuttle.name}
              </Typography>
              <Chip 
                label={data.schedule.status === 'scheduled' ? t('driver.scheduled') : data.schedule.status}
                color={data.schedule.status === 'scheduled' ? 'success' : 'default'}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarMonth sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {format(departureDate, 'EEEE, d MMMM yyyy', { locale: dateLocale })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Schedule sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="h6" color="primary">
                    {data.schedule.departure_time}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {data.schedule.pickup_location}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      → {data.schedule.destination}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('driver.occupancy')}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h4" color={occupancyColor}>
                      {data.summary.total_seats_booked} / {data.shuttle.capacity}
                    </Typography>
                    <Typography variant="h5" color={occupancyColor}>
                      {data.summary.occupancy_percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={data.summary.occupancy_percentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: 'grey.300',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: data.summary.occupancy_percentage > 80 ? 'warning.main' : 'success.main',
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {data.summary.available_seats} {t('driver.seatsAvailable')}
                </Typography>
              </Box>
            </Box>

            {data.schedule.notes && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">{data.schedule.notes}</Typography>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Passenger List */}
        <Paper elevation={3} sx={{ p: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <Groups sx={{ mr: 1 }} />
              {t('driver.passengerList')} ({data.summary.total_passengers})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('driver.lastUpdate')}: {format(lastRefresh, 'HH:mm:ss')}
            </Typography>
          </Box>

          {data.passengers.length === 0 ? (
            <Alert severity="info" sx={{ mx: 1 }}>
              {t('driver.noPassengers')}
            </Alert>
          ) : (
            <List sx={{ p: 0 }}>
              {data.passengers.map((passenger, index) => (
                <React.Fragment key={index}>
                  <ListItem sx={{ px: { xs: 1, sm: 2 } }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Person color={passenger.checked_in ? 'success' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {passenger.nickname}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={`${passenger.seats} ${passenger.seats > 1 ? t('driver.seats') : t('driver.seat')}`}
                              size="small"
                              color="primary"
                            />
                            <Typography variant="caption" color="text.secondary">
                              #{passenger.booking_code}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box component="span">
                          {passenger.phone && (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Phone sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                              <Typography component="span" variant="body2" color="text.secondary">
                                {passenger.phone}
                              </Typography>
                            </Box>
                          )}
                          {passenger.notes && (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>
                              <Notes sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary', mt: 0.3 }} />
                              <Typography component="span" variant="body2" color="text.secondary">
                                {passenger.notes}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                  {index < data.passengers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          <Box sx={{ mt: 2, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('driver.totalPassengers')}: {data.summary.total_passengers} | 
              {' '}{t('driver.totalSeats')}: {data.summary.total_seats_booked}
            </Typography>
          </Box>
        </Paper>
      </Container>
  );
};

export default DriverViewPage;